import {ActionList, ActionMenu, Button, TextInput, Tooltip, VisuallyHidden} from '@primer/react'
import {LockIcon, OrganizationIcon, PersonIcon, TriangleDownIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode} from 'react'
import {announce} from '@github-ui/aria-live'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {generateSSOUrl} from '@github-ui/global-sso-banner-ui/generate-sso-url'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useOverlayDirection} from '@github-ui/copilot-chat-contexts/OverlayDirectionContext'
import {useLocation} from '@github-ui/react-router'

import {
  isOrganizationSandboxEnabled,
  type AvailableOrganization,
  type TaskComposerScope,
} from '@github-ui/one-copilot-composer/shared'
import {navigateToSsoUrl} from './navigate-to-sso-url'
import {setPendingSsoOrgLogin} from '@github-ui/one-copilot-composer/pending-sso-org'
import styles from './ScopePicker.module.css'

/**
 * Leading visual for the trigger and menu items. Renders the entity avatar when
 * available, falling back to an octicon matching the entity kind.
 */
function ScopeAvatar({
  src,
  alt,
  kind,
  size,
}: {
  src: string | undefined
  alt: string
  kind: 'user' | 'organization'
  size: number
}) {
  if (src) {
    return <GitHubAvatar src={src} alt={alt} size={size} square={kind === 'organization'} />
  }
  const FallbackIcon = kind === 'organization' ? OrganizationIcon : PersonIcon
  return <FallbackIcon size={size} aria-label={alt || undefined} aria-hidden={alt ? undefined : true} />
}

interface ScopePickerProps {
  /** Currently selected scope. */
  selectedScope: TaskComposerScope
  /** Called when the user picks a new scope. Not called when SSO gating intervenes. */
  onSelectScope: (scope: TaskComposerScope) => void
  /** Retained unavailable owner that remains effective until the user selects another scope. */
  retainedOwnerLogin?: string
  /** Organizations the user can pick from. */
  organizations: AvailableOrganization[]
  /** Current user login, used for the personal-scope accessible label + avatar fallback. */
  currentUserLogin: string
  /** Current user avatar URL, used for the personal-scope leading visual. */
  currentUserAvatarUrl?: string
  /** Optional class applied to the trigger button. */
  triggerClassName?: string
  /** Optional disabled flag forwarded to the trigger button. */
  disabled?: boolean
  /** Trigger size, matched to the surface that contains the picker. */
  size?: 'small' | 'medium'
}

function useScopeOwnerSelection({
  selectedScope,
  onSelectScope,
  retainedOwnerLogin,
  currentUserLogin,
  currentUserAvatarUrl,
  presentation,
  onBeforeSsoNavigate,
}: Pick<
  ComposerScopePickerState,
  'selectedScope' | 'onSelectScope' | 'retainedOwnerLogin' | 'currentUserLogin' | 'currentUserAvatarUrl'
> & {presentation?: 'split_picker'; onBeforeSsoNavigate?: () => void}) {
  const location = useLocation()
  const {triggerLabel, triggerAvatarSrc, triggerAvatarAlt, accessibleScopeLabel} = useMemo(() => {
    if (retainedOwnerLogin) {
      return {
        triggerLabel: retainedOwnerLogin,
        triggerAvatarSrc: undefined,
        triggerAvatarAlt: `${retainedOwnerLogin} avatar`,
        accessibleScopeLabel: retainedOwnerLogin,
      }
    }
    if (selectedScope.kind === 'organization') {
      const org = selectedScope.organization
      return {
        triggerLabel: org.login,
        triggerAvatarSrc: org.avatarUrl,
        triggerAvatarAlt: `${org.login} avatar`,
        accessibleScopeLabel: org.login,
      }
    }
    return {
      triggerLabel: 'Personal',
      triggerAvatarSrc: currentUserAvatarUrl,
      triggerAvatarAlt: `${currentUserLogin} avatar`,
      accessibleScopeLabel: `Personal (${currentUserLogin})`,
    }
  }, [selectedScope, currentUserLogin, currentUserAvatarUrl, retainedOwnerLogin])

  const emitScopeEvent = useCallback(
    (target: string, action: 'shown' | 'click' | 'select', extra?: Record<string, unknown>) => {
      sendEvent('agent_sessions.task_view', {
        target,
        action,
        ...(presentation ? {presentation} : {}),
        ...extra,
      })
    },
    [presentation],
  )

  const handleSelectPersonal = () => {
    if (selectedScope.kind === 'personal' && !retainedOwnerLogin) {
      onSelectScope({kind: 'personal'})
      return
    }
    emitScopeEvent('SCOPE_PICKER_ITEM_PERSONAL', 'select')
    onSelectScope({kind: 'personal'})
  }

  const handleSelectOrg = (org: AvailableOrganization) => {
    if (!isOrganizationSandboxEnabled(org)) return
    if (org.requiresSso) {
      emitScopeEvent('SCOPE_PICKER_ITEM_ORG', 'click', {requiresSso: true, orgLogin: org.login})
      onBeforeSsoNavigate?.()
      setPendingSsoOrgLogin(org.login)
      navigateToSsoUrl(generateSSOUrl(org.login, location))
      return
    }
    if (selectedScope.kind === 'organization' && selectedScope.organization.id === org.id) {
      onSelectScope({kind: 'organization', organization: org})
      return
    }
    emitScopeEvent('SCOPE_PICKER_ITEM_ORG', 'select', {orgLogin: org.login})
    onSelectScope({kind: 'organization', organization: org})
  }

  return {
    triggerLabel,
    triggerAvatarSrc,
    triggerAvatarAlt,
    accessibleScopeLabel,
    emitScopeEvent,
    handleSelectPersonal,
    handleSelectOrg,
  }
}

/**
 * Scope selector for the TaskComposer. Selecting an org that requires SSO opens
 * the SSO flow with `return_to` set to the current URL; the caller re-hydrates
 * scope on return.
 */
export function ScopePicker({
  selectedScope,
  onSelectScope,
  retainedOwnerLogin,
  organizations,
  currentUserLogin,
  currentUserAvatarUrl,
  triggerClassName,
  disabled,
  size = 'medium',
}: ScopePickerProps) {
  const [open, setOpen] = useState(false)
  const preferredOverlaySide = useOverlayDirection()
  const tooltipDirection = preferredOverlaySide === 'outside-top' || preferredOverlaySide === 'inside-top' ? 'n' : 's'

  const {
    triggerLabel,
    triggerAvatarSrc,
    triggerAvatarAlt,
    accessibleScopeLabel,
    emitScopeEvent,
    handleSelectPersonal,
    handleSelectOrg,
  } = useScopeOwnerSelection({
    selectedScope,
    onSelectScope,
    retainedOwnerLogin,
    currentUserLogin,
    currentUserAvatarUrl,
  })

  const ariaLabel = `Select conversation scope, current: ${accessibleScopeLabel}`

  const triggerButton = (
    <Button
      type="button"
      leadingVisual={() => (
        <span className={styles.avatarLeadingVisual}>
          <ScopeAvatar
            src={triggerAvatarSrc}
            alt={triggerAvatarAlt}
            kind={retainedOwnerLogin || selectedScope.kind === 'organization' ? 'organization' : 'user'}
            size={16}
          />
        </span>
      )}
      trailingAction={TriangleDownIcon}
      aria-label={ariaLabel}
      variant="invisible"
      size={size}
      className={clsx(styles.selectorButton, triggerClassName)}
      disabled={disabled}
    >
      {triggerLabel}
    </Button>
  )

  return (
    <ActionMenu
      open={open}
      onOpenChange={next => {
        setOpen(next)
        if (next) emitScopeEvent('SCOPE_PICKER', 'shown')
      }}
    >
      <ActionMenu.Anchor>
        {/*
         * The label truncates in narrow composers, so the tooltip is the only way to
         * read the full owner. It uses `description` so it supplements the button's
         * accessible name instead of replacing it, and it overlays rather than
         * reflowing so the environment strip stays one line tall. Tooltip rejects a
         * non-interactive child, and a disabled trigger cannot be hovered or focused
         * anyway, so that case renders the bare button. A mouse click leaves focus on
         * the anchor, so the tooltip is disabled while the menu is open to stop it
         * covering the menu.
         */}
        {disabled ? (
          triggerButton
        ) : (
          <Tooltip
            text={accessibleScopeLabel}
            type="description"
            direction={tooltipDirection}
            _privateDisableTooltip={open}
          >
            {triggerButton}
          </Tooltip>
        )}
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium" maxHeight="medium" overflow="auto" side={preferredOverlaySide}>
        <ActionList selectionVariant="single">
          <ActionList.Item
            selected={selectedScope.kind === 'personal' && !retainedOwnerLogin}
            onSelect={handleSelectPersonal}
          >
            <ActionList.LeadingVisual>
              <ScopeAvatar src={currentUserAvatarUrl} alt={`${currentUserLogin} avatar`} kind="user" size={20} />
            </ActionList.LeadingVisual>
            {currentUserLogin}
          </ActionList.Item>
          {organizations.map(org => {
            const isSelected = selectedScope.kind === 'organization' && selectedScope.organization.id === org.id
            // The scope only applies to Sandbox, so an org that disabled it stays
            // listed with the reason rather than vanishing from the user's orgs.
            const sandboxDisabled = !isOrganizationSandboxEnabled(org)
            const showsSsoGate = org.requiresSso && !sandboxDisabled
            return (
              <ActionList.Item
                key={org.id}
                selected={showsSsoGate ? undefined : isSelected}
                role={showsSsoGate ? 'menuitem' : undefined}
                inactiveText={sandboxDisabled ? 'Sandbox is disabled by this organization' : undefined}
                onSelect={() => handleSelectOrg(org)}
              >
                <ActionList.LeadingVisual>
                  <ScopeAvatar src={org.avatarUrl} alt={`${org.login} avatar`} kind="organization" size={20} />
                </ActionList.LeadingVisual>
                {org.login}
                {showsSsoGate && (
                  <ActionList.TrailingVisual>
                    <span className={styles.itemMeta}>
                      <LockIcon size={12} aria-label="SSO required" />
                    </span>
                  </ActionList.TrailingVisual>
                )}
              </ActionList.Item>
            )
          })}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

/**
 * The scope state the new-session composer hands its host.
 *
 * Declared structurally rather than imported so this module does not depend on
 * the composer package; it is assignable to the composer's `scopePicker` prop
 * either way.
 */
export interface ComposerScopePickerState {
  selectedScope: TaskComposerScope
  onSelectScope: (scope: TaskComposerScope) => void
  retainedOwnerLogin?: string
  organizations: AvailableOrganization[]
  currentUserLogin: string
  currentUserAvatarUrl?: string
  disabled?: boolean
}

export interface ComposerScopeOwnerPanelState extends ComposerScopePickerState {
  onBeforeSsoNavigate: () => void
}

export function ScopeOwnerPanel({
  selectedScope,
  onSelectScope,
  onBeforeSsoNavigate,
  retainedOwnerLogin,
  organizations,
  currentUserLogin,
  currentUserAvatarUrl,
  disabled,
}: ComposerScopeOwnerPanelState) {
  const [query, setQuery] = useState('')
  const headingId = useId()
  const searchId = useId()
  const didReportShownRef = useRef(false)
  const {emitScopeEvent, handleSelectPersonal, handleSelectOrg} = useScopeOwnerSelection({
    selectedScope,
    onSelectScope,
    onBeforeSsoNavigate,
    retainedOwnerLogin,
    currentUserLogin,
    currentUserAvatarUrl,
    presentation: 'split_picker',
  })

  const onPanelMount = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || didReportShownRef.current) return
      didReportShownRef.current = true
      emitScopeEvent('SCOPE_PICKER', 'shown')
    },
    [emitScopeEvent],
  )

  const normalizedQuery = query.trim().toLowerCase()
  const showPersonal = 'personal'.includes(normalizedQuery) || currentUserLogin.toLowerCase().includes(normalizedQuery)
  const filteredOrganizations = organizations.filter(org => org.login.toLowerCase().includes(normalizedQuery))
  const ownerCount = filteredOrganizations.length + (showPersonal ? 1 : 0)
  useEffect(() => {
    if (!normalizedQuery) return
    const timeout = setTimeout(() => {
      announce(ownerCount === 0 ? 'No owners found' : `${ownerCount} ${ownerCount === 1 ? 'owner' : 'owners'}`, {
        assertive: false,
      })
    }, 300)
    return () => clearTimeout(timeout)
  }, [normalizedQuery, ownerCount])

  return (
    <div className={styles.ownerPanel} ref={onPanelMount}>
      <div className={styles.ownerHeader}>
        <h2 className={styles.ownerHeading} id={headingId}>
          Session scope
        </h2>
        <p className={styles.ownerSubtitle}>Scope determines which repositories the session can access</p>
        <label className={styles.ownerSearchLabel} htmlFor={searchId}>
          Search owners
        </label>
        <TextInput
          id={searchId}
          value={query}
          onChange={event => setQuery(event.target.value)}
          disabled={disabled}
          className={styles.ownerSearch}
        />
      </div>
      <div className={styles.ownerList}>
        <ActionList role="listbox" aria-labelledby={headingId} selectionVariant="single">
          {showPersonal && (
            <ActionList.Item
              role="option"
              selected={selectedScope.kind === 'personal' && !retainedOwnerLogin}
              onSelect={handleSelectPersonal}
              disabled={disabled}
            >
              <ActionList.LeadingVisual>
                <ScopeAvatar src={currentUserAvatarUrl} alt="" kind="user" size={20} />
              </ActionList.LeadingVisual>
              Personal
              <VisuallyHidden> ({currentUserLogin})</VisuallyHidden>
              <ActionList.Description variant="block">Access to personal repositories</ActionList.Description>
            </ActionList.Item>
          )}
          {filteredOrganizations.map(org => {
            const sandboxDisabled = !isOrganizationSandboxEnabled(org)
            const showsSsoGate = org.requiresSso && !sandboxDisabled
            return (
              <ActionList.Item
                key={org.id}
                role="option"
                selected={
                  !showsSsoGate &&
                  !sandboxDisabled &&
                  selectedScope.kind === 'organization' &&
                  selectedScope.organization.id === org.id
                }
                onSelect={() => handleSelectOrg(org)}
                disabled={disabled}
                inactiveText={sandboxDisabled ? 'Sandbox is disabled by this organization' : undefined}
              >
                <ActionList.LeadingVisual>
                  <ScopeAvatar src={org.avatarUrl} alt="" kind="organization" size={20} />
                </ActionList.LeadingVisual>
                {org.login}
                <ActionList.Description variant="block">
                  {showsSsoGate ? 'Single sign-on required' : 'Organization scope'}
                </ActionList.Description>
                {showsSsoGate && (
                  <ActionList.TrailingVisual>
                    <LockIcon size={12} aria-hidden="true" />
                  </ActionList.TrailingVisual>
                )}
              </ActionList.Item>
            )
          })}
        </ActionList>
        {normalizedQuery && ownerCount === 0 && <p>No owners found</p>}
      </div>
    </div>
  )
}

export function renderComposerScopeOwnerPanel(state: ComposerScopeOwnerPanelState): ReactNode {
  return <ScopeOwnerPanel {...state} />
}

/**
 * The organization scope control every host of the new-session composer places.
 *
 * The composer decides *whether* a scope can be picked — it is offered for a
 * repoless Sandbox draft, Chat and task alike — while the host supplies the
 * control. The Agents page and the dashboard both render it from here so the two
 * surfaces cannot drift into offering different scope behavior for the same
 * draft.
 */
export function renderComposerScopePicker(state: ComposerScopePickerState): ReactNode {
  return (
    <ScopePicker
      selectedScope={state.selectedScope}
      onSelectScope={state.onSelectScope}
      retainedOwnerLogin={state.retainedOwnerLogin}
      organizations={state.organizations}
      currentUserLogin={state.currentUserLogin}
      currentUserAvatarUrl={state.currentUserAvatarUrl}
      size="small"
      disabled={state.disabled}
    />
  )
}
